import {
  BasketballSwitchRequest,
  BasketballSwitchRequestPossibleCoachGames,
  BasketballSwitchRequestPossibleCoachPractices,
  BasketballSwitchRequestPossibleJudgeGame,
  BasketballSwitchRequestPossibleRefereeGames,
  BasketballTeamTaskAssignment,
  SelectOption,
} from '@hefe/models';
import { formatDateTime } from '@hefe/utils';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { t } from '@hefe/i18n';
import API from '../../../api/api';
import {
  BasketballSwitchRequestCreateRequest,
  BasketballSwitchRequestTakeOverJudgeGameRequest,
  BasketballSwitchRequestTakeOverRequest,
} from '../../../models/requests/basketball-switch-request';

const FEATURE_KEY = 'basketballSwitchRequests';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */

export const fetchBasketballUserSwitchRequests = createAsyncThunk(
  'basketballSwitchRequests/getUserRequests',
  async (id: number, thunkAPI) => {
    const response = await API.basketballSwitchRequests().userSwitchRequests(
      id
    );
    return {
      userId: id,
      data: response?.data,
    };
  }
);

export const createBasketballSwitchRequest = createAsyncThunk(
  'basketballSwitchRequests/create',
  async (data: BasketballSwitchRequestCreateRequest, thunkAPI) => {
    const response = await API.basketballSwitchRequests().create(data);

    return response?.data;
  }
);

export const takeOverBasketballSwitchRequest = createAsyncThunk(
  'basketballSwitchRequests/takeOver',
  async (data: BasketballSwitchRequestTakeOverRequest, thunkAPI) => {
    const response = await API.basketballSwitchRequests().takeOver(data);

    return response?.data;
  }
);

export const takeOverBasketballJudgeGameSwitchRequest = createAsyncThunk(
  'basketballSwitchRequests/takeOverJudgeGame',
  async (data: BasketballSwitchRequestTakeOverJudgeGameRequest, thunkAPI) => {
    const response = await API.basketballSwitchRequests().takeOverJudges(data);

    return response?.data;
  }
);

export const deleteBasketballSwitchRequest = createAsyncThunk<
  any,
  BasketballSwitchRequest
>(
  'basketballSwitchRequests/delete',
  async (request: BasketballSwitchRequest, thunkAPI) => {
    const response = await API.basketballSwitchRequests().delete(request.id);
    if (response) {
      return thunkAPI.fulfillWithValue(request);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const fetchBasketballUserPossibleJudgeGames = createAsyncThunk(
  'basketballSwitchRequests/getPossibleJudgeGames',
  async (id: number, thunkAPI) => {
    const response = await API.basketballSwitchRequests().possibleJudgeGames(
      id
    );
    return {
      userId: id,
      data: response?.data,
    };
  }
);

export const fetchBasketballUserPossibleTasks = createAsyncThunk(
  'basketballSwitchRequests/getPossibleTasks',
  async (id: number, thunkAPI) => {
    const response =
      await API.basketballTeamTaskAssignments().getUserAssignments(id);
    return {
      userId: id,
      data: response?.data,
    };
  }
);

export const fetchBasketballCoachPossibleGames = createAsyncThunk(
  'basketballSwitchRequests/getPossibleCoachGames',
  async (id: number, thunkAPI) => {
    const response = await API.basketballSwitchRequests().possibleCoachGames(
      id
    );
    return {
      userId: id,
      data: response?.data,
    };
  }
);

export const fetchBasketballCoachPossiblePractices = createAsyncThunk(
  'basketballSwitchRequests/getPossibleCoachPractices',
  async (id: number, thunkAPI) => {
    const response =
      await API.basketballSwitchRequests().possibleCoachPractices(id);
    return {
      userId: id,
      data: response?.data,
    };
  }
);

export const fetchBasketballRefereePossibleGames = createAsyncThunk(
  'basketballSwitchRequests/getPossibleRefereeGames',
  async (id: number, thunkAPI) => {
    const response = await API.basketballSwitchRequests().possibleRefereeGames(
      id
    );
    return {
      userId: id,
      data: response?.data,
    };
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface BasketballSwitchRequestsState {
  switchRequests: { [key: number]: BasketballSwitchRequest[] };
  possibleJudgeGames: {
    [key: number]: BasketballSwitchRequestPossibleJudgeGame[];
  };
  possibleCoachGames: {
    [key: number]: BasketballSwitchRequestPossibleCoachGames[];
  };
  possibleCoachPractices: {
    [key: number]: BasketballSwitchRequestPossibleCoachPractices[];
  };
  possibleRefereeGames: {
    [key: number]: BasketballSwitchRequestPossibleRefereeGames[];
  };
  possibleTasks: {
    [key: number]: BasketballTeamTaskAssignment[];
  };
}

const initialState: BasketballSwitchRequestsState = {
  switchRequests: {},
  possibleJudgeGames: {},
  possibleCoachGames: {},
  possibleCoachPractices: {},
  possibleRefereeGames: {},
  possibleTasks: {},
};

export const basketballSwitchRequestsSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    /******************************************************************************************
     *
     *                                  CRUD SWITCH REQUESTS
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballUserSwitchRequests.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<{
          userId: number;
          data: BasketballSwitchRequest[];
        }>
      ) => {
        state.switchRequests[action.payload.userId] = action.payload.data;
      }
    );
    builder.addCase(
      createBasketballSwitchRequest.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<BasketballSwitchRequest>
      ) => {
        const userRequests = state.switchRequests[action.payload.user_id];
        for (const userKey in state.switchRequests) {
          if (parseInt(userKey) === action.payload.user_id) {
            // Add request
            if (
              userRequests.findIndex(
                (request) => request.id === action.payload.id
              ) > -1
            ) {
              state.switchRequests[userKey] = userRequests.map((request) =>
                request.id === action.payload.id ? action.payload : request
              );
            } else {
              state.switchRequests[userKey].push(action.payload);
            }
          } else {
            // Wipe data
            delete state.switchRequests[userKey];
          }
        }
      }
    );
    builder.addCase(
      takeOverBasketballSwitchRequest.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<BasketballSwitchRequest>
      ) => {
        for (const userKey in state.switchRequests) {
          state.switchRequests[userKey] = state.switchRequests[userKey].map(
            (request) =>
              request.id === action.payload.id ? action.payload : request
          );
        }
      }
    );
    builder.addCase(
      takeOverBasketballJudgeGameSwitchRequest.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<BasketballSwitchRequest>
      ) => {
        for (const userKey in state.switchRequests) {
          state.switchRequests[userKey] = state.switchRequests[userKey].map(
            (request) =>
              request.id === action.payload.id ? action.payload : request
          );
        }
      }
    );
    builder.addCase(
      deleteBasketballSwitchRequest.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<BasketballSwitchRequest>
      ) => {
        for (const userKey in state.switchRequests) {
          state.switchRequests[userKey] = state.switchRequests[userKey].filter(
            (request) => request.id !== action.payload.id
          );
        }
      }
    );
    /******************************************************************************************
     *
     *                                     JUDGE GAMES
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballUserPossibleJudgeGames.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<{
          userId: number;
          data: BasketballSwitchRequestPossibleJudgeGame[];
        }>
      ) => {
        state.possibleJudgeGames[action.payload.userId] = action.payload.data;
      }
    );
    /******************************************************************************************
     *
     *                                     USER TASKS
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballUserPossibleTasks.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<{
          userId: number;
          data: BasketballTeamTaskAssignment[];
        }>
      ) => {
        state.possibleTasks[action.payload.userId] = action.payload.data;
      }
    );
    /******************************************************************************************
     *
     *                                     COACH SWITCHES
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballCoachPossibleGames.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<{
          userId: number;
          data: BasketballSwitchRequestPossibleCoachGames[];
        }>
      ) => {
        state.possibleCoachGames[action.payload.userId] = action.payload.data;
      }
    );
    builder.addCase(
      fetchBasketballCoachPossiblePractices.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<{
          userId: number;
          data: BasketballSwitchRequestPossibleCoachPractices[];
        }>
      ) => {
        state.possibleCoachPractices[action.payload.userId] =
          action.payload.data;
      }
    );
    /******************************************************************************************
     *
     *                                     REFEREE SWITCHES
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballRefereePossibleGames.fulfilled,
      (
        state: BasketballSwitchRequestsState,
        action: PayloadAction<{
          userId: number;
          data: BasketballSwitchRequestPossibleRefereeGames[];
        }>
      ) => {
        state.possibleRefereeGames[action.payload.userId] = action.payload.data;
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballSwitchRequestsReducer =
  basketballSwitchRequestsSlice.reducer;
export const basketballSwitchRequestsActions =
  basketballSwitchRequestsSlice.actions;

const state = (rootState: any): BasketballSwitchRequestsState =>
  rootState[FEATURE_KEY];

export const basketballSwitchRequestsState = createSelector(
  state,
  (state) => state
);

export const switchRequestsSelector = createSelector(state, (state) => {
  return state.switchRequests;
});

export const possibleJudgeGamesSelector = createSelector(state, (state) => {
  return state.possibleJudgeGames;
});

export const possibleTasksSelector = createSelector(
  state,
  (state): { [key: number]: SelectOption[] } => {
    const users: any = {};

    for (const key in state.possibleTasks) {
      users[key] = state.possibleTasks[key].map((assignment) => {
        const date = assignment.game
          ? assignment.game.date
          : assignment.practice?.date || '';
        const labelText = assignment.game ? t('games.singular') : t('practice');
        const label = `${labelText} - ${assignment.task.name} (${formatDateTime(
          date
        )})`;
        const eventId = assignment.game
          ? assignment.game.id
          : assignment.practice?.id;
        return {
          label: label,
          value: `${assignment.id}:${eventId}:${assignment.task.team_id}:${
            assignment.game ? '1' : '0'
          }`,
        };
      });
    }

    return users;
  }
);

export const possibleCoachGamesSelector = createSelector(
  state,
  (state): { [key: number]: SelectOption[] } => {
    const users: any = {};

    for (const key in state.possibleCoachGames) {
      users[key] = state.possibleCoachGames[key].map((game) => {
        return {
          value: `${game.id}:${game.team.id}`,
          label: `${game.team.name}: ${game.matchup} (${formatDateTime(
            game.date
          )})`,
        };
      });
    }

    return users;
  }
);

export const possibleCoachPracticesSelector = createSelector(
  state,
  (state): { [key: number]: SelectOption[] } => {
    const users: any = {};

    for (const key in state.possibleCoachPractices) {
      users[key] = state.possibleCoachPractices[key].map((practice) => {
        return {
          value: `${practice.id}:${practice.team.id}`,
          label: `${practice.team.name} - ${formatDateTime(practice.date)}`,
        };
      });
    }

    return users;
  }
);

export const possibleRefereeGamesSelector = createSelector(
  state,
  (state): { [key: number]: SelectOption[] } => {
    const users: any = {};

    for (const key in state.possibleRefereeGames) {
      users[key] = state.possibleRefereeGames[key].map((game) => {
        return {
          value: `${game.id}`,
          label: `${game.matchup} (${formatDateTime(game.date)})`,
        };
      });
    }

    return users;
  }
);
